<template>
    <TicketsUIContainer
        v-if="recentTickets.length"
        :tickets="recentTickets"
        :highlightedTickets="highlightedTickets"
        :isRecreatingTicket="isRecreatingTicket"
        @ticketOpen="onTicketOpen"
        @ticketClick="onTicketClick"
        @detailsClick="onDetailsClick"
        @recreate="onRecreateTicket"
    />
</template>

<script>
    import {
        forEach as _forEach,
    } from '@lodash';
    import { mapActions, mapGetters } from 'vuex';
    import Modals from '@store/modules/ui/shared/modal/modals';
    import { errorGuard } from '@app/utils';
    import DateUtils from '@core/utils/date/DateUtils';
    import { TicketType, EventType, TicketStatus } from '@core/models/tickets/enums';
    import { isSpecialEvent } from '@core/models/specials/SpecialEvent';
    import LottoService from '@core/services/lotto/LottoService';
    import TicketsUIContainer from './TicketsUIContainer.vue';

    export default {
        name: 'TicketsWidgetContainer',
        data() {
            /**
             * Logic behind liveEvents is:
             * 1. We save live flag for all the open tickets
             * 2. We listen to allChanges incrementId to change, that way we now how to do recheck
             * 3. We then check our events if they have changed because we only need isOngoing and isFinished flags
             *
             * ticketId1: {
             *     eventId1: true
             * }
             * ticketId2: {
             *     eventId1: true
             * }
             */
            return {
                matchResults: {},
                liveEvents: {},
                recreatableTickets: {},
            };
        },
        async created() {
            await errorGuard({
                action: async () => {
                    await this.loadActiveTickets();
                    this.subscribeToTicketsUpdates();
                }
            });

            this.createRecreatableTickets();
        },
        watch: {
            allChangesIncrementId() {
                this.updateLiveFlags();
            },
            tickets(newValue, oldValue) {
                if (newValue && !oldValue) {
                    this.tickets.forEach((ticket) => {
                        this.createLiveFlags(ticket);
                    });
                } else if (newValue && oldValue) {
                    this.updateLiveFlags();
                }

                this.createRecreatableTickets();
            }
        },
        computed: {
            ...mapGetters('data/sportOffer', [
                'eventsMap',
                'allChangesIncrementId',
            ]),
            ...mapGetters('ui/ticketsStack', [
                'tickets',
                'highlightedTickets',
                'isRecreatingTicket',
            ]),
            recentTickets() {
                return this.tickets.map((ticket) =>
                    ({
                        ...ticket,
                        events: ticket.events.map((ev) => {
                            const event = this.eventsMap[ev.eventId];
                            if (event && event.isOngoing() && ev.type === EventType.live) {
                                ev.liveData = event.liveData;
                                ev.sportId = event.sportId;
                            }
                            const results = this.matchResults[ev.eventId];

                            const res = results && results.result;

                            return ({
                                ...ev,
                                result: res,
                            });
                        }),
                        liveEvents: this.liveEvents[ticket.id],
                        hasOngoingEvents: this.hasOngoingEvents(ticket.id),
                        canRecreate: this.recreatableTickets[ticket.id] || false,
                    }));
            },
        },
        methods: {
            ...mapActions('data/tickets', [
                'getTicketEventsDetails',
            ]),
            ...mapActions('ui/ticketsStack', [
                'loadActiveTickets',
                'toggleHighlightedTicket',
                'subscribeToTicketsUpdates',
                'unsubscribeFromTicketsUpdates'
            ]),
            ...mapActions('ui/ticketsStack', [
                'recreateTicket',
            ]),
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            getTicketById(ticketId) {
                return this.tickets.find(({ id }) => id === ticketId);
            },
            createLiveFlags(ticket) {
                if (ticket.type !== TicketType.sport) {
                    return;
                }
                const liveFlags = {};

                ticket.events.forEach((ticketEvent) => {
                    if (!isSpecialEvent(ticketEvent.eventId) && ticketEvent.type === EventType.live) {
                        const event = this.eventsMap[ticketEvent.eventId];
                        liveFlags[ticketEvent.eventId] = !!event && event.isOngoing();
                    }
                });

                this.$set(this.liveEvents, ticket.id, liveFlags);
            },
            updateLiveFlags() {
                const activeTicketsMap = this.tickets.reduce(
                    (a, ticket) => {
                        a[ticket.id] = true;
                        return a;
                    },
                    {}
                );

                _forEach(this.liveEvents, (flags, ticketId) => {
                    if (!activeTicketsMap[ticketId]) {
                        delete this.liveEvents[ticketId];
                        return;
                    }
                    _forEach(flags, (isLive, eventId) => {
                        const event = this.eventsMap[eventId];

                        if (!event) {
                            return;
                        }

                        if (event && event.isFinished() && isLive) {
                            this.fetchResultsForFinishedEvents(ticketId);
                        }
                        this.$set(flags, eventId, this.eventsMap[eventId].isOngoing());
                    });
                });

                this.tickets.forEach((ticket) => {
                    if (!this.liveEvents[ticket.id]) {
                        this.createLiveFlags(ticket);
                    }
                });
            },
            async fetchResultsForFinishedEvents(ticketId) {
                const ticket = this.getTicketById(ticketId);
                const eventsIds = ticket.events.map((e) => e.eventId);
                const eventsDetails = await this.getTicketEventsDetails(eventsIds);
                eventsDetails.forEach((event) => this.$set(this.matchResults, event.id, event.matchResults));
            },
            hasOngoingEvents(ticketId) {
                if (!this.liveEvents || !this.liveEvents[ticketId]) {
                    return false;
                }
                return Object.values(this.liveEvents[ticketId]).some(Boolean);
            },
            async onTicketOpen(ticketId) {
                this.createRecreatableTickets();
                await errorGuard({
                    action: async () => {
                        await this.fetchResultsForFinishedEvents(ticketId);
                    },
                });
            },
            onTicketClick(id) {
                this.toggleHighlightedTicket({ key: id, value: false });
            },
            onDetailsClick(ticketId) {
                const ticket = this.getTicketById(ticketId);
                this.setModal({
                    code: Modals.singleTicket.code,
                    data: {
                        ticket
                    }
                });
            },
            onRecreateTicket(ticket) {
                this.recreateTicket(ticket);
            },
            isFinished(ticket) {
                return [
                    TicketStatus.refund,
                    TicketStatus.win,
                    TicketStatus.lost,
                    TicketStatus.payed,
                ].includes(ticket.status);
            },
            createRecreatableTickets() {
                const now = new Date();
                this.tickets.forEach(async (ticket) => {
                    if (this.isFinished(ticket)) {
                        this.recreatableTickets[ticket.id] = false;
                        return;
                    }

                    if (ticket.type === TicketType.sport) {
                        this.$set(this.recreatableTickets, ticket.id, true);
                    } else {
                        const events = await LottoService.getInstance()
                            .getLottoEventDetails([parseInt(ticket.events[0].eventId, 10)]);
                        const lottoEvent = events[0];
                        this.$set(this.recreatableTickets, ticket.id, DateUtils.isBefore(now, lottoEvent.endBet));
                    }
                });
            }
        },
        beforeDestroy() {
            this.unsubscribeFromTicketsUpdates();
        },
        components: {
            TicketsUIContainer,
        },
    };
</script>



// WEBPACK FOOTER //
// src/app/modules/ticketsWidget/TicketsWidgetContainer.vue